// File: app/src/apis/llm/callTimeLimit.js
//
// A time limit for one provider call (#167). Without one, a call that stalls
// waits until the browser or the cloud proxy gives up (two minutes through
// /proxy, never for the Gemini SDK, which talks to Google directly), and only
// then does the retry start. A generation on 8 Oct 2026 ran for over six
// minutes and never finished.
//
// Two limits, because a streamed call shows its progress and a plain one
// doesn't:
//  - QUIET: no reply and no streamed thinking for this long ends the call.
//    Each streamed chunk restarts the clock, so a model that is visibly
//    thinking is left alone.
//  - TOTAL: a call that keeps trickling forever still ends here.
// Either one throws CallTimeoutError, which the retry loop treats like any
// other failed attempt. It is deliberately not an AbortError: that name means
// the person pressed Stop, and stops the retries.
//
// Kept out of providers.js so Jest can import it without the ESM-only
// Gemini SDK.

export const CALL_QUIET_LIMIT_MS = 90 * 1000;
export const CALL_TOTAL_LIMIT_MS = 4 * 60 * 1000;

export class CallTimeoutError extends Error {
  constructor(message, { limitMs, quiet } = {}) {
    super(message);
    this.name = "CallTimeoutError";
    this.limitMs = limitMs;
    this.quiet = quiet;
  }
}

/**
 * Run one provider call under a time limit.
 *
 * @param {(signal: AbortSignal, touch: () => void) => Promise<any>} run
 *   Starts the call with the signal it must honour. Calling `touch` says the
 *   call is still making progress and restarts the quiet clock.
 * @param {object} [options]
 * @param {AbortSignal|null} [options.signal] the person's Stop button
 * @param {number} [options.quietMs]
 * @param {number} [options.totalMs]
 */
export const withCallTimeLimit = (
  run,
  {
    signal = null,
    quietMs = CALL_QUIET_LIMIT_MS,
    totalMs = CALL_TOTAL_LIMIT_MS,
  } = {},
) => {
  const controller = new AbortController();
  let quietTimer = null;
  let totalTimer = null;
  let rejectTimeout;
  let settled = false;

  const onStop = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener("abort", onStop, { once: true });
  }

  const cleanup = () => {
    settled = true;
    clearTimeout(quietTimer);
    clearTimeout(totalTimer);
    if (signal) signal.removeEventListener("abort", onStop);
  };

  const timeOut = (limitMs, quiet) => {
    if (settled) return;
    const seconds = Math.round(limitMs / 1000);
    rejectTimeout(
      new CallTimeoutError(
        quiet
          ? `The AI sent nothing for ${seconds} s.`
          : `The AI took longer than ${seconds} s.`,
        { limitMs, quiet },
      ),
    );
    // Cancel the request too, so a stalled stream doesn't keep a connection
    // (and, on some providers, the bill) running behind the retry.
    controller.abort();
  };

  const touch = () => {
    if (settled) return;
    clearTimeout(quietTimer);
    quietTimer = setTimeout(() => timeOut(quietMs, true), quietMs);
  };

  // Raced rather than left to the provider: not every SDK checks its signal
  // while it waits for the first byte.
  const timeout = new Promise((_, reject) => {
    rejectTimeout = reject;
  });
  totalTimer = setTimeout(() => timeOut(totalMs, false), totalMs);
  touch();

  return Promise.race([
    Promise.resolve().then(() => run(controller.signal, touch)),
    timeout,
  ]).finally(cleanup);
};
